import { api, apiForSession, getAuthSession } from './client';
import type { ChatDto, MessageDto } from './types';
import type { operations } from './generated/openapi';
import type { AdminUser } from './admin';

function supportApi<T>(path: string, options: RequestInit = {}): Promise<T> {
  const session = getAuthSession();
  if (!session) return Promise.reject(new DOMException('Сессия отсутствует', 'AbortError'));
  return apiForSession<T>(session, path, options);
}

export type SupportNotificationPrefs = operations['getSupportNotificationPrefs']['responses'][200]['content']['application/json'];

let notificationPrefsCache: { session: string; prefs: SupportNotificationPrefs } | null = null;
let notificationPrefsVersion = 0;

export function cachedSupportNotificationPrefs(): SupportNotificationPrefs | null {
  return notificationPrefsCache?.session === getAuthSession() ? notificationPrefsCache.prefs : null;
}

export async function getSupportNotificationPrefs(): Promise<SupportNotificationPrefs> {
  const session = getAuthSession();
  const version = notificationPrefsVersion;
  const prefs = await supportApi<SupportNotificationPrefs>('/api/v1/support/notification-prefs');
  if (session && session === getAuthSession() && version === notificationPrefsVersion) notificationPrefsCache = { session, prefs };
  return prefs;
}

export async function updateSupportNotificationPrefs(prefs: SupportNotificationPrefs): Promise<SupportNotificationPrefs> {
  const session = getAuthSession();
  ++notificationPrefsVersion;
  const saved = await supportApi<SupportNotificationPrefs>('/api/v1/support/notification-prefs', { method: 'PUT', body: JSON.stringify(prefs) });
  ++notificationPrefsVersion;
  if (session && session === getAuthSession()) notificationPrefsCache = { session, prefs: saved };
  return saved;
}

export type SupportConversationPage = operations['listSupportConversations']['responses'][200]['content']['application/json'];

/** Получить свою переписку; сервер атомарно создаёт её при первом обращении. */
export function getSupportConversation(): Promise<ChatDto> {
  return supportApi('/api/v1/support/conversation');
}

export function listSupportConversations(before?: string | null): Promise<SupportConversationPage> {
  const query = new URLSearchParams({ limit: '50' });
  if (before) query.set('before', before);
  return supportApi('/api/v1/support/conversations?' + query);
}

/** Обычная обратная связь идёт в личную переписку, диагностика — в операторский поток. */
export function submitSupportRequest(text: string, technicalDiagnostic = false): Promise<MessageDto> {
  return supportApi<MessageDto>('/api/v1/support/messages', {
    method: 'POST',
    body: JSON.stringify({ text, ...(technicalDiagnostic ? { technicalDiagnostic: true } : {}) }),
  });
}

/** Обращение в поддержку из публичной формы на экране входа — без авторизации
 *  (сценарий «не могу войти в аккаунт»). Email обязателен: на него пришлют ответ. */
export interface SupportTicketDto {
  id: string;
  email: string;
  message: string;
  status: string;
  createdAt: string;
}

export function createSupportTicket(email: string, message: string): Promise<{ id: string; status: string; createdAt: string }> {
  return api('/api/v1/support/ticket', { method: 'POST', body: JSON.stringify({ email, message }) });
}

export function adminListTickets(): Promise<SupportTicketDto[]> {
  return api('/api/v1/admin/support/tickets');
}

export function adminUpdateTicketStatus(id: string, status: 'open' | 'done') {
  return api(`/api/v1/admin/support/tickets/${id}`, { method: 'PATCH', body: JSON.stringify({ status }) });
}

// Роль техподдержки (support): в админке доступны только пользователи и обращения — отдельные
// эндпоинты (SupportController), чтобы поддержка не видела чаты/медиа/бэкапы/настройки.
export function supportListUsers(q?: string, limit = 50): Promise<AdminUser[]> {
  return api(`/api/v1/admin/support/users?limit=${limit}${q ? '&q=' + encodeURIComponent(q) : ''}`);
}

export function supportSetDisplayName(id: string, displayName: string) {
  return api(`/api/v1/admin/support/users/${id}/display-name`, { method: 'PATCH', body: JSON.stringify({ displayName }) });
}

export function supportResetPassword(id: string, newPassword: string) {
  return api(`/api/v1/admin/support/users/${id}/reset-password`, { method: 'POST', body: JSON.stringify({ newPassword }) });
}
